import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { fetchRolesForMultiSelect } from './userActions';
import UserModal from './UserModal';
import Table from '../table/redux/Table';
import { ApiUser } from '../api';
import { userApi } from '../apis';

import './Users.scss';
import { SalvButton, SalvSwitch, Size } from '@salvapp/ui-components';
import SalvPage from '../common/SalvPage';
import { showConfirmationModal } from '../confirmation-modal/confirmationModalReducer';
import { showFeedback } from '../feedback/feedbackReducer';
import { setRefresh } from '../table/redux/tablesReducer';
import { CAN_DEACTIVATE_ALL_USERS } from '../auth/permissions';
import Has from '../auth/Has';
import { useMe } from '../organisation/useMe';

export const Users = () => {
  const [showModal, setShowModal] = useState(false);
  const [user, setUser] = useState<ApiUser | null>(null);
  const [includeInactive, setIncludeInactive] = useState<boolean>(false);
  const { currentOrganisation } = useMe();
  const dispatch = useDispatch();

  const fetchAll = (page: number, size: number, query?: string) => {
    return userApi.findAllUsers({ page, size, query, includeInactive });
  };

  const onComplete = async () => {
    setShowModal(false);
    setUser(null);
  };

  const addUser = async () => {
    setUser(null);
    setShowModal(true);
    await dispatch(fetchRolesForMultiSelect(currentOrganisation?.id));
  };

  const disableAllUsers = async () => {
    dispatch(
      showConfirmationModal({
        title: 'Are you sure?',
        description: 'All users in this organisation will be deactivated and cannot log in to Salv anymore.',
        confirmationText: 'Yes, deactivate the users!',
        cancelText: 'No',
        onConfirm: async () => {
          await userApi.deactivateAllUsers();
          dispatch(showFeedback({ message: 'All users have been deactivated.' }));
          dispatch(setRefresh({ name: 'users', shouldRefresh: true }));
        },
      })
    );
  };

  const editUser = async (user: ApiUser) => {
    setUser(user);
    setShowModal(true);
    await dispatch(fetchRolesForMultiSelect(user?.organisation?.id));
  };

  return (
    <SalvPage title={'Users'}>
      <Table<ApiUser>
        hover={false}
        name='users'
        fetchContent={fetchAll}
        searchable
        actions={() => (
          <div className={'d-flex align-items-center'}>
            <SalvSwitch
              className={'me-2'}
              selected={includeInactive}
              onChange={() => setIncludeInactive(!includeInactive)}
              data-testid='includeInactiveSwitch'
              label={'Show deactivated'}
            />
            <SalvButton
              onClick={() => addUser()}
              className={'me-2'}
              variant='muted'
              size={Size.EXTRA_SMALL}
              label={'Add new user'}
            />
            <Has allPermissions={CAN_DEACTIVATE_ALL_USERS}>
              {currentOrganisation && (
                <SalvButton
                  onClick={() => disableAllUsers()}
                  className={'me-2'}
                  variant='danger-outline'
                  size={Size.EXTRA_SMALL}
                  label={'Deactivate all users'}
                />
              )}
            </Has>
          </div>
        )}
        header={() => (
          <tr>
            <th>ID</th>
            <th>Name and Email</th>
            <th>Roles</th>
            <th>Organisation</th>
            <th className='text-end'>Actions</th>
          </tr>
        )}
        body={(users) =>
          users.map((u) => (
            <tr key={u.id} className={(u.inactive && 'text-muted') || ''}>
              <td>{u.id}</td>
              <td>
                <div className='name-email'>
                  <span className='name'>{u.name}</span>
                  <span className='email'>{u.email}</span>
                </div>
              </td>
              <td className='roles'>{u.roles.map((role) => role.name).join(', ')}</td>
              <td>{u.organisation ? u.organisation.name : ''}</td>
              <td className='text-end'>
                <SalvButton
                  onClick={() => editUser(u)}
                  variant='muted'
                  className='right-button'
                  size={Size.EXTRA_SMALL}
                  label={'Edit'}
                />
              </td>
            </tr>
          ))
        }
      />
      <UserModal show={showModal} user={user} onComplete={onComplete} />
    </SalvPage>
  );
};

export default Users;
